import React, { memo } from "react";

import { components } from "@api/schema";
import { IAppCurrency } from "@App/types";
import { Text } from "@UI/Components/Text/Text";
import { Rate } from "@Shared/Components/Rate/Rate";
import { Money } from "@Shared/Components/Money/Money";

type TBillingUnit = components["schemas"]["serviceOffer"]["billingUnit"];
type TColorScheme = "candidateCard" | "conversationHeader";

interface IExpense {
  amountCents: number;
}

export interface IOfferPriceProps {
  rateCents: number;
  serviceFeesCents?: number;
  expenses?: IExpense[];
  expensesFeesCents?: number;
  billingUnit: TBillingUnit;
  currency: IAppCurrency;
  isSubscribed: boolean;
  colorScheme: TColorScheme;
  textDecoration?: string;
}

const roundToEuro = (cents: number): number => Math.round(cents / 100) * 100;

export const OfferPrice = memo(
  ({
    rateCents,
    serviceFeesCents = 0,
    expenses = [],
    expensesFeesCents = 0,
    billingUnit,
    currency,
    isSubscribed,
    textDecoration,
  }: IOfferPriceProps) => {
    const color = "gray.800";

    const displayRateCents = roundToEuro(
      isSubscribed ? rateCents : rateCents + serviceFeesCents
    );

    const rawExpensesCents = expenses.reduce((sum, e) => sum + e.amountCents, 0);
    const displayExpensesCents = roundToEuro(
      rawExpensesCents + (isSubscribed ? 0 : expensesFeesCents)
    );
    const showExpenses = displayExpensesCents > 0;

    if (billingUnit === "flat" && showExpenses) {
      return (
        <Text as="span" color={color} textDecoration={textDecoration} m="0">
          <Money cents={displayRateCents + displayExpensesCents} currency={currency} />
        </Text>
      );
    }

    return (
      <Text as="span" color={color} textDecoration={textDecoration} m="0">
        <Rate cents={displayRateCents} unit={billingUnit} currency={currency} />
        {showExpenses && (
          <Text as="span" fontWeight="normal" m="0">
            {" + "}
            <Money cents={displayExpensesCents} currency={currency} />
          </Text>
        )}
      </Text>
    );
  }
);

OfferPrice.displayName = "OfferPrice";
